/**
 * @file useOrderFeed.ts
 * @description Данные ленты заказа: фильтр, страницы по курсору, счётчики
 * (phase19.31; контракт cabinet-api-contract.md §9 `GET /admin/orders/:id/feed`).
 *
 * Счётчики чипов приходят с каждой страницей и от фильтра не зависят — их
 * не считаем сами (один источник правды). Перечитывание при возврате на
 * вкладку тихое: прежние строки остаются на экране, пока идёт запрос, а
 * число строк сохраняется (до 50), чтобы «Показать ещё» не схлопнулось.
 * Ответы устаревших запросов (сменили фильтр, пока шёл прошлый) отбрасываются.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { useNotify } from "../components/ui/NotifyProvider";
import { ApiError } from "../lib/api-client";
import {
  getOrderFeed,
  type OrderFeedCountersDto,
  type OrderFeedFilter,
  type OrderFeedItemDto,
} from "../lib/api-orders";

/** Размер страницы — «Показать ещё 20». */
export const ORDER_FEED_PAGE = 20;
/** Предел сервера на страницу. */
const ORDER_FEED_MAX = 50;

export interface OrderFeedState {
  filter: OrderFeedFilter;
  setFilter: (filter: OrderFeedFilter) => void;
  items: OrderFeedItemDto[];
  counters: OrderFeedCountersDto | null;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  /** Ошибка первой загрузки (строк нет). */
  error: string | null;
  loadMore: () => void;
  /** Перечитать тихо — поверх прежних строк. */
  reload: () => void;
}

/** Текст ошибки для человека: код сервера не показываем. */
function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.status === 404) return "Заказ не найден — возможно, его удалили";
  if (err instanceof ApiError && err.status === 403) return "Нет доступа к ленте заказа";
  return fallback;
}

export function useOrderFeed(orderId: string): OrderFeedState {
  // Ссылка, а не зависимость: перечитывание не должно зависеть от объекта уведомлений.
  const notifyRef = useRef(useNotify());
  const [filter, setFilterState] = useState<OrderFeedFilter>("all");
  const [items, setItems] = useState<OrderFeedItemDto[]>([]);
  const [counters, setCounters] = useState<OrderFeedCountersDto | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Номер последнего запроса первой страницы: ответы прежних отбрасываем.
  const requestRef = useRef(0);
  const shownRef = useRef(0);
  shownRef.current = items.length;

  const loadFirst = useCallback(
    async (kind: OrderFeedFilter, silent: boolean) => {
      const request = ++requestRef.current;
      setLoading(true);
      if (!silent) setError(null);
      const limit = silent ? Math.min(ORDER_FEED_MAX, Math.max(ORDER_FEED_PAGE, shownRef.current)) : ORDER_FEED_PAGE;
      try {
        const page = await getOrderFeed(orderId, { kind, limit });
        if (request !== requestRef.current) return;
        setItems(page.items);
        setCursor(page.nextCursor);
        setCounters(page.counters);
        setError(null);
      } catch (err) {
        if (request !== requestRef.current) return;
        const text = errorText(err, "Не удалось загрузить ленту. Заказ и документы доступны");
        // Строки уже на экране — не стираем их, а говорим уведомлением.
        if (silent && shownRef.current > 0) notifyRef.current.error("Не удалось обновить ленту. Показаны прежние события");
        else setError(text);
      } finally {
        if (request === requestRef.current) setLoading(false);
      }
    },
    [orderId],
  );

  useEffect(() => {
    setItems([]);
    void loadFirst(filter, false);
    // Смена фильтра или заказа — первая страница заново.
  }, [filter, loadFirst]);

  const setFilter = useCallback((next: OrderFeedFilter) => setFilterState(next), []);
  const reload = useCallback(() => void loadFirst(filter, true), [filter, loadFirst]);

  const loadMore = useCallback(() => {
    if (!cursor || loadingMore) return;
    const request = requestRef.current;
    setLoadingMore(true);
    getOrderFeed(orderId, { kind: filter, before: cursor, limit: ORDER_FEED_PAGE })
      .then((page) => {
        if (request !== requestRef.current) return;
        // Сервер отдаёт строго более старые; дубли по id всё равно отсекаем.
        setItems((prev) => {
          const seen = new Set(prev.map((i) => i.id));
          return [...prev, ...page.items.filter((i) => !seen.has(i.id))];
        });
        setCursor(page.nextCursor);
        setCounters(page.counters);
      })
      .catch(() => notifyRef.current.error("Не удалось загрузить более ранние события. Попробуйте ещё раз"))
      .finally(() => setLoadingMore(false));
  }, [cursor, loadingMore, orderId, filter]);

  return { filter, setFilter, items, counters, hasMore: cursor !== null, loading, loadingMore, error, loadMore, reload };
}
